<template>
  <div>
    <div class="d-flex align-center mb-6">
      <h2 class="white--text font-weight-bold">Planes</h2>
      <v-spacer></v-spacer>
      <v-btn color="blue" @click="abrirCrear">
        <v-icon left>mdi-plus</v-icon> Nuevo plan
      </v-btn>
    </div>

    <v-row>
      <v-col
        v-for="plan in planes"
        :key="plan.id"
        cols="12"
        md="4"
      >
        <v-card dark class="rounded-lg elevation-3 pa-4" style="border: 1px solid rgba(255,255,255,0.08)">
          <div class="d-flex justify-space-between align-center mb-2">
            <div>
              <div class="text-h6 font-weight-bold white--text">{{ plan.nombre }}</div>
              <div class="caption grey--text text--lighten-1">{{ plan.codigo }}</div>
            </div>
            <v-chip :color="plan.activo ? 'green' : 'grey'" small dark>
              {{ plan.activo ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </div>

          <div class="body-2 grey--text mb-2">{{ plan.descripcion || '—' }}</div>

          <div v-if="plan.precio != null" class="d-flex align-center mb-4">
            <span class="text-h6 font-weight-black white--text">
              {{ plan.moneda || '' }} {{ plan.precio | formatPrecio }}
            </span>
            <span class="caption grey--text ml-1">/ mes</span>
          </div>

          <div class="mb-2">
            <div class="caption grey--text mb-1">MÓDULOS</div>
            <div v-if="plan.modulos && plan.modulos.length">
              <v-chip
                v-for="modulo in plan.modulos"
                :key="modulo.id"
                small
                class="mr-1 mb-1"
                color="blue darken-4"
                dark
              >{{ modulo.nombre }}</v-chip>
            </div>
            <div v-else class="caption grey--text">Sin módulos asignados</div>
          </div>

          <div class="mb-4">
            <div class="caption grey--text mb-1">FEATURES</div>
            <div v-if="plan.features && plan.features.length">
              <v-chip
                v-for="feature in plan.features"
                :key="feature.id"
                small
                class="mr-1 mb-1"
                color="teal darken-3"
                dark
              >{{ feature.nombre }}</v-chip>
            </div>
            <div v-else class="caption grey--text">Sin features asignadas</div>
          </div>

          <div class="d-flex justify-end">
            <v-btn icon small @click="abrirEditar(plan)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small color="red" @click="confirmarEliminar(plan)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <v-col v-if="!planes.length && !loading" cols="12">
        <div class="text-center grey--text py-8">No hay planes registrados.</div>
      </v-col>
    </v-row>

    <v-progress-circular v-if="loading" indeterminate color="blue" class="d-block mx-auto mt-8"></v-progress-circular>

    <!-- Dialog crear/editar -->
    <v-dialog v-model="dialog" max-width="560" dark>
      <v-card>
        <v-card-title>{{ editando ? 'Editar plan' : 'Nuevo plan' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="form.codigo"
            label="Código"
            outlined
            dense
            class="mb-2"
          ></v-text-field>
          <v-text-field
            v-model="form.nombre"
            label="Nombre"
            outlined
            dense
            class="mb-2"
          ></v-text-field>
          <v-textarea
            v-model="form.descripcion"
            label="Descripción"
            outlined
            dense
            rows="2"
            class="mb-2"
          ></v-textarea>
          <v-autocomplete
            v-model="form.modulo_ids"
            :items="modulos"
            item-text="nombre"
            item-value="id"
            label="Módulos"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            class="mb-2"
          ></v-autocomplete>
          <v-autocomplete
            v-model="form.feature_ids"
            :items="featuresDisponibles"
            item-text="nombre"
            item-value="id"
            label="Features"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
          >
            <template v-slot:item="{ item }">
              <div>
                <div>{{ item.nombre }}</div>
                <div class="caption grey--text">{{ item.codigo }}</div>
              </div>
            </template>
          </v-autocomplete>
          <v-row dense class="mb-2">
            <v-col cols="8">
              <v-text-field
                v-model.number="form.precio"
                label="Precio"
                type="number"
                min="0"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="form.moneda"
                :items="['ARS', 'USD', 'EUR']"
                label="Moneda"
                outlined
                dense
              ></v-select>
            </v-col>
          </v-row>
          <v-switch v-model="form.activo" label="Activo" color="blue"></v-switch>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="blue" :loading="guardando" @click="guardar">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm delete -->
    <v-dialog v-model="dialogEliminar" max-width="400" dark>
      <v-card>
        <v-card-title>¿Eliminar plan?</v-card-title>
        <v-card-text>
          Se eliminará el plan <strong>{{ planAEliminar && planAEliminar.nombre }}</strong>.
          Las suscripciones activas con este plan podrían verse afectadas.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogEliminar = false">Cancelar</v-btn>
          <v-btn color="red" :loading="eliminando" @click="eliminar">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'PlanesLista',
  filters: {
    formatPrecio(value) {
      if (value == null) return '—'
      return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2 }).format(value)
    },
  },
  data() {
    return {
      dialog: false,
      editando: false,
      guardando: false,
      planId: null,
      dialogEliminar: false,
      eliminando: false,
      planAEliminar: null,
      form: {
        codigo: '',
        nombre: '',
        descripcion: '',
        activo: true,
        precio: null,
        moneda: 'ARS',
        modulo_ids: [],
        feature_ids: [],
      },
    }
  },
  computed: {
    ...mapState('suscripciones', ['planes', 'modulos', 'features', 'loading']),
    featuresDisponibles() {
      // Filtrar features según los módulos seleccionados en el form
      if (!this.form.modulo_ids.length) return this.features
      const modulosSeleccionados = this.modulos
        .filter(m => this.form.modulo_ids.includes(m.id))
        .map(m => m.codigo)
      return this.features.filter(f => modulosSeleccionados.includes(f.modulo))
    },
  },
  methods: {
    ...mapActions('suscripciones', [
      'fetchPlanes',
      'fetchModulos',
      'fetchFeatures',
      'createPlan',
      'updatePlan',
      'deletePlan',
    ]),
    abrirCrear() {
      this.editando = false
      this.planId = null
      this.form = { codigo: '', nombre: '', descripcion: '', activo: true, precio: null, moneda: 'ARS', modulo_ids: [], feature_ids: [] }
      this.dialog = true
    },
    abrirEditar(plan) {
      this.editando = true
      this.planId = plan.id
      this.form = {
        codigo: plan.codigo,
        nombre: plan.nombre,
        descripcion: plan.descripcion || '',
        activo: plan.activo,
        precio: plan.precio ?? null,
        moneda: plan.moneda || 'ARS',
        modulo_ids: plan.modulos ? plan.modulos.map(m => m.id) : [],
        feature_ids: plan.features ? plan.features.map(f => f.id) : [],
      }
      this.dialog = true
    },
    async guardar() {
      this.guardando = true
      try {
        if (this.editando) {
          await this.updatePlan({ id: this.planId, data: this.form })
        } else {
          await this.createPlan(this.form)
        }
        this.dialog = false
        this.$store.dispatch('alerts/showSnackbar', { text: 'Plan guardado', color: 'success', icon: 'mdi-check' })
      } catch (e) {
        this.$store.dispatch('alerts/showSnackbar', { text: 'Error al guardar', color: 'error', icon: 'mdi-alert' })
      } finally {
        this.guardando = false
      }
    },
    confirmarEliminar(plan) {
      this.planAEliminar = plan
      this.dialogEliminar = true
    },
    async eliminar() {
      this.eliminando = true
      try {
        await this.deletePlan(this.planAEliminar.id)
        this.dialogEliminar = false
        this.$store.dispatch('alerts/showSnackbar', { text: 'Plan eliminado', color: 'success', icon: 'mdi-check' })
      } catch (e) {
        this.$store.dispatch('alerts/showSnackbar', { text: 'Error al eliminar', color: 'error', icon: 'mdi-alert' })
      } finally {
        this.eliminando = false
      }
    },
  },
  created() {
    this.fetchPlanes()
    this.fetchModulos()
    this.fetchFeatures()
  },
}
</script>
